/**
 * Component manager module for handling component lifecycle and initialization.
 * Provides centralized management of all site components and their instances.
 * @module core/managers
 */

import ee from './ee'

// Import components
import Header from './components/maybourne/header'
import Footer from './components/maybourne/footer'
import ProductContents from './components/maybourne/product-contents'
import FeaturedProducts from './components/maybourne/featured-products'
import AnnouncementBar from './components/maybourne/announcement-bar'
import NewsletterPopup from './components/maybourne/newsletter-popup'
import OftenBoughtTogether from './components/maybourne/often-bought-together'
import TakeoverCarousel from './components/maybourne/takeover-carousel'
import CartPage from './components/maybourne/cart-page'
import ProductModals from './components/maybourne/product-modals'
import BuyButtons from './components/maybourne/buy-buttons'
import ProductGallery from './components/maybourne/product-gallery'
import CollectionToolbar from './components/maybourne/collection-toolbar'

/**
 * @typedef {Object} ComponentInstances
 * @property {Object|null} header - Header component
 * @property {Object|null} footer - Footer component
 * @property {Object|null} productContents - Product contents component
 * @property {Object|null} oftenBoughtTogether - Often bought together component
 * @property {Object|null} cartPage - Cart page component
 * @property {Object|null} productModals - Product modals component
 * @property {Object|null} featuredProducts - Featured products component
 * @property {Object|null} announcementBar - Announcement bar component
 * @property {Object|null} newsletterPopup - Newsletter popup component
 * @property {Object|null} buyButtons - Buy buttons component
 * @property {Object|null} productGallery - Product gallery component
 * @property {Array|null} takeoverCarousels - Takeover carousel components
 */

class MaybourneManager {
  constructor() {
    /** @type {ComponentInstances} */
    this.instances = {
      // Initialize component instances
      header: null,
      footer: null,
      productContents: null,
      oftenBoughtTogether: null,
      cartPage: null,
      productModals: null,
      featuredProducts: null,
      announcementBar: null,
      newsletterPopup: null,
      buyButtons: null,
      productGallery: null,
      collectionToolbar: null,
      takeoverCarousels: [],
    }

    this.bindEvents()
  }

  /**
   * Bind event listeners for component lifecycle
   */
  bindEvents() {
    ee.addListener('initComponents', this.initComponents.bind(this))
    ee.addListener('destroyComponents', this.destroyComponents.bind(this))
    
    // Listen for cart drawer refresh to reinitialize components
    document.addEventListener('cart-drawer:refreshed', () => {
      if (this.instances.oftenBoughtTogether) {
        this.instances.oftenBoughtTogether.refresh()
      } else {
        this.initOftenBoughtTogether()
      }
    })
    
    // Watch for cart drawer content changes using MutationObserver
    this.setupCartDrawerObserver()
  }
  
  /**
   * Set up MutationObserver to watch for cart drawer content changes
   */
  setupCartDrawerObserver() {
    // Wait for DOM to be ready
    const initObserver = () => {
      const observer = new MutationObserver((mutations) => {
        let shouldReinitialize = false
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            // Check if any added nodes contain often-bought-together elements
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node
                
                // Skip if this change is happening inside an existing often-bought-together container
                const isInsideContainer = element.closest('.maybourne-often-bought-together-container')
                if (isInsideContainer) {
                  return // Don't reinitialize for changes inside existing containers
                }
                
                if (element.classList && element.classList.contains('maybourne-often-bought-together-container')) {
                  shouldReinitialize = true
                } else if (element.querySelector && element.querySelector('.maybourne-often-bought-together-container')) {
                  shouldReinitialize = true
                }
              }
            })
          }
        })
        
        if (shouldReinitialize) {
          if (this.instances.oftenBoughtTogether) {
            this.instances.oftenBoughtTogether.refresh()
          } else {
            this.initOftenBoughtTogether()
          }
        }
      })
      
      // Start observing the document for changes
      if (document.body) {
        observer.observe(document.body, {
          childList: true,
          subtree: true
        })
              }
    }
    
    // Initialize observer when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initObserver)
    } else {
      initObserver()
    }
  }

  /**
   * Initialize multiple instances of a component
   * @template T
   * @param {string} selector - CSS selector for the component
   * @param {new (element: HTMLElement) => T} ComponentClass - Component constructor
   * @returns {T[]}
   */
  initMultipleComponents(selector, ComponentClass) {
    const elements = document.querySelectorAll(selector)
    if (!elements.length) {
      return []
    }

    return Array.from(elements)
      .filter((element) => element instanceof Element)
      .map((element) => {
        try {
          return new ComponentClass(element)
        } catch (error) {
          console.error(`Failed to initialize component for element:`, element, error)
          return null
        }
      })
      .filter(Boolean)
  }

  /**
   * Initialize a single instance of a component
   * @template T
   * @param {new () => T} ComponentClass - Component constructor
   * @returns {T}
   */
  initSingleComponent(ComponentClass) {
    return new ComponentClass()
  }

  // Component initialization methods
  // Add your component initializations here

  /**
   * Initialize header component
   * @param {Document|HTMLElement} container - Container element
   * @returns {Header}
   */
  initHeader(container = document) {
    if (this.instances.header) {
      return this.instances.header
    }
    
    try {
      this.instances.header = new Header(container)
      return this.instances.header
    } catch (error) {
      console.error('Failed to initialize header:', error)
      return null
    }
  }

  /**
   * Initialize footer component
   * @param {Document|HTMLElement} container - Container element
   * @returns {Footer}
   */
  initFooter(container = document) {
    if (this.instances.footer) {
      return this.instances.footer
    }
    
    try {
      this.instances.footer = new Footer(container)
      return this.instances.footer
    } catch (error) {
      console.error('Failed to initialize footer:', error)
      return null
    }
  }


  /**
   * Initialize product contents component
   * @param {Document|HTMLElement} container - Container element
   * @returns {ProductContents}
   */
  initProductContents(container = document) {
    if (this.instances.productContents) {
      return this.instances.productContents
    }
    
    try {
      this.instances.productContents = new ProductContents(container)
      return this.instances.productContents
    } catch (error) {
      console.error('Failed to initialize product contents:', error)
      return null
    }
  }

  /**
   * Initialize often bought together component
   * @param {Document|HTMLElement} container - Container element
   * @returns {OftenBoughtTogether}
   */
  initOftenBoughtTogether(container = document) {
    if (this.instances.oftenBoughtTogether) {
      return this.instances.oftenBoughtTogether
    }
    
    try {
      this.instances.oftenBoughtTogether = new OftenBoughtTogether(container)
      return this.instances.oftenBoughtTogether
    } catch (error) {
      console.error('Failed to initialize often bought together:', error)
      return null
    }
  }

  /**
   * Initialize cart page component
   * @param {Document|HTMLElement} container - Container element
   * @returns {CartPage}
   */
  initCartPage(container = document) {
    if (this.instances.cartPage) {
      return this.instances.cartPage
    }
    
    try {
      this.instances.cartPage = new CartPage(container)
      return this.instances.cartPage
    } catch (error) {
      console.error('Failed to initialize cart page:', error)
      return null
    }
  }

  /**
   * Initialize product modals component
   * @param {Document|HTMLElement} container - Container element
   * @returns {ProductModals}
   */
  initProductModals(container = document) {
    if (this.instances.productModals) {
      return this.instances.productModals
    }
    
    try {
      this.instances.productModals = new ProductModals(container)
      return this.instances.productModals
    } catch (error) {
      console.error('Failed to initialize product modals:', error)
      return null
    }
  }

  /**
   * Initialize featured products component
   * @param {Document|HTMLElement} container - Container element
   * @returns {FeaturedProducts}
   */
  initFeaturedProducts(container = document) {
    if (this.instances.featuredProducts) {
      return this.instances.featuredProducts
    }
    
    try {
      const featuredProductsContainers = container.querySelectorAll('[id*="Maybourne-Featured-Products"]')
      if (featuredProductsContainers.length > 0) {
        this.instances.featuredProducts = new FeaturedProducts(featuredProductsContainers[0])
        return this.instances.featuredProducts
      }
    } catch (error) {
      console.error('Failed to initialize featured products:', error)
      return null
    }
  }

  /**
   * Initialize announcement bar component
   * @param {Document|HTMLElement} container - Container element
   * @returns {AnnouncementBar}
   */
  initAnnouncementBar(container = document) {
    if (this.instances.announcementBar) {
      return this.instances.announcementBar
    }
    
    try {
      this.instances.announcementBar = new AnnouncementBar(container)
      return this.instances.announcementBar
    } catch (error) {
      console.error('Failed to initialize announcement bar:', error)
      return null
    }
  }

  /**
   * Initialize newsletter popup component
   * @param {Document|HTMLElement} container - Container element
   * @returns {NewsletterPopup}
   */
  initNewsletterPopup(container = document) {
    if (this.instances.newsletterPopup) {
      return this.instances.newsletterPopup
    }
    
    try {
      this.instances.newsletterPopup = new NewsletterPopup(container)
      return this.instances.newsletterPopup
    } catch (error) {
      console.error('Failed to initialize newsletter popup:', error)
      return null
    }
  }

  /**
   * Initialize takeover carousel components
   * @param {Document|HTMLElement} container - Container element
   * @returns {Array<TakeoverCarousel>}
   */
  initTakeoverCarousels(container = document) {
    // Always reinitialize to handle dynamic content
    const elements = container.querySelectorAll('.maybourne-takeover-carousel')
    if (!elements.length) {
      this.instances.takeoverCarousels = []
      return []
    }
    
    try {
      this.instances.takeoverCarousels = this.initMultipleComponents('.maybourne-takeover-carousel', TakeoverCarousel)
      return this.instances.takeoverCarousels
    } catch (error) {
      console.error('Failed to initialize takeover carousels:', error)
      return []
    }
  }

  /**
   * Initialize buy buttons component
   * @param {Document|HTMLElement} container - Container element
   * @returns {BuyButtons}
   */
  initBuyButtons(container = document) {
    if (this.instances.buyButtons) {
      return this.instances.buyButtons
    }
    
    try {
      this.instances.buyButtons = new BuyButtons()
      return this.instances.buyButtons
    } catch (error) {
      console.error('Failed to initialize buy buttons:', error)
      return null
    }
  }

  /**
   * Initialize product gallery component
   * @param {Document|HTMLElement} container - Container element
   * @returns {ProductGallery}
   */
  initProductGallery(container = document) {
    if (this.instances.productGallery) {
      return this.instances.productGallery
    }
    
    try {
      this.instances.productGallery = new ProductGallery(container)
      return this.instances.productGallery
    } catch (error) {
      console.error('Failed to initialize product gallery:', error)
      return null
    }
  }

  /**
   * Initialize all components
   * @param {Document|HTMLElement} container - Container element
   */
  initComponents(container = document) {
    // Call component initialization methods
    this.initHeader(container)
    this.initFooter(container)
    this.initProductContents(container)
    this.initOftenBoughtTogether(container)
    this.initCartPage(container)
    this.initProductModals(container)
    this.initFeaturedProducts(container)
    this.initAnnouncementBar(container)
    this.initNewsletterPopup(container)
    this.initBuyButtons(container)
    this.initTakeoverCarousels(container)
    this.initProductGallery(container)
    this.initCollectionToolbar(container)

    console.log('All components initialized')
  }

  /**
   * Initialize collection toolbar behavior
   * @param {Document|HTMLElement} container
   * @returns {CollectionToolbar}
   */
  initCollectionToolbar(container = document) {
    if (this.instances.collectionToolbar) {
      return this.instances.collectionToolbar
    }
    try {
      this.instances.collectionToolbar = new CollectionToolbar(container)
      return this.instances.collectionToolbar
    } catch (error) {
      console.error('Failed to initialize collection toolbar:', error)
      return null
    }
  }

  /**
   * Destroy header component
   */
  destroyHeader() {
    if (!this.instances.header) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.header.destroy === 'function') {
      this.instances.header.destroy()
    }
    
    this.instances.header = null
  }

  /**
   * Destroy footer component
   */
  destroyFooter() {
    if (!this.instances.footer) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.footer.destroy === 'function') {
      this.instances.footer.destroy()
    }
    
    this.instances.footer = null
  }



  /**
   * Destroy product contents component
   */
  destroyProductContents() {
    if (!this.instances.productContents) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.productContents.destroy === 'function') {
      this.instances.productContents.destroy()
    }
    
    this.instances.productContents = null
  }

  /**
   * Destroy often bought together component
   */
  destroyOftenBoughtTogether() {
    if (!this.instances.oftenBoughtTogether) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.oftenBoughtTogether.destroy === 'function') {
      this.instances.oftenBoughtTogether.destroy()
    }
    
    this.instances.oftenBoughtTogether = null
  }

  /**
   * Destroy cart page component
   */
  destroyCartPage() {
    if (!this.instances.cartPage) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.cartPage.destroy === 'function') {
      this.instances.cartPage.destroy()
    }
    
    this.instances.cartPage = null
  }

  /**
   * Destroy product modals component
   */
  destroyProductModals() {
    if (!this.instances.productModals) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.productModals.destroy === 'function') {
      this.instances.productModals.destroy()
    }
    
    this.instances.productModals = null
  }

  /**
   * Destroy featured products component
   */
  destroyFeaturedProducts() {
    if (!this.instances.featuredProducts) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.featuredProducts.destroy === 'function') {
      this.instances.featuredProducts.destroy()
    }
    
    this.instances.featuredProducts = null
  }

  /**
   * Destroy announcement bar component
   */
  destroyAnnouncementBar() {
    if (!this.instances.announcementBar) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.announcementBar.destroy === 'function') {
      this.instances.announcementBar.destroy()
    }
    
    this.instances.announcementBar = null
  }

  /**
   * Destroy newsletter popup component
   */
  destroyNewsletterPopup() {
    if (!this.instances.newsletterPopup) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.newsletterPopup.destroy === 'function') {
      this.instances.newsletterPopup.destroy()
    }
    
    this.instances.newsletterPopup = null
  }

  /**
   * Destroy takeover carousel components
   */
  destroyTakeoverCarousels() {
    if (!this.instances.takeoverCarousels || !Array.isArray(this.instances.takeoverCarousels)) return
    
    // Clean up all carousel instances
    this.instances.takeoverCarousels.forEach(carousel => {
      if (carousel && typeof carousel.destroy === 'function') {
        carousel.destroy()
      }
    })
    
    this.instances.takeoverCarousels = null
  }

  /**
   * Destroy buy buttons component
   */
  destroyBuyButtons() {
    if (!this.instances.buyButtons) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.buyButtons.destroy === 'function') {
      this.instances.buyButtons.destroy()
    }
    
    this.instances.buyButtons = null
  }

  /**
   * Destroy product gallery component
   */
  destroyProductGallery() {
    if (!this.instances.productGallery) return
    
    // Clean up event listeners if needed
    if (typeof this.instances.productGallery.destroy === 'function') {
      this.instances.productGallery.destroy()
    }
    
    this.instances.productGallery = null
  }

  /**
   * Destroy all components
   */
  destroyComponents() {
    // Call component destruction methods
    this.destroyHeader()
    this.destroyFooter()
    this.destroyProductContents()
    this.destroyOftenBoughtTogether()
    this.destroyCartPage()
    this.destroyProductModals()
    this.destroyFeaturedProducts()
    this.destroyAnnouncementBar()
    this.destroyNewsletterPopup()
    this.destroyBuyButtons()
    this.destroyTakeoverCarousels()
    this.destroyProductGallery()

    console.log('All components destroyed')
  }

  // Component destruction methods
  // Add your component destruction methods here
}

// Create and export a singleton instance
const componentManager = new MaybourneManager()
export default componentManager
